import { html, nothing } from "lit";
import type {
  FleetGraphBindingDraft,
  FleetGraphConfigDraft,
} from "../controllers/fleet-graph-config.ts";
import type {
  FleetGraphAgentDraft,
  FleetGraphBehaviorDraft,
  FleetGraphEditorMode,
  FleetGraphEditorTab,
} from "../controllers/fleet-graph-editor.ts";
import type { SkillStatusReport } from "../types.ts";
import { FLEET_GRAPH_INBOUND_NODE_ID } from "../chat/agent-fleet-graph-model.ts";
import {
  AGENT_TOOL_PROFILE_OPTIONS,
  resolveConfiguredModels,
} from "../controllers/agents-list-config-patch.ts";
import { icons } from "../icons.ts";

export type FleetGraphEditorModalProps = {
  open: boolean;
  mode: FleetGraphEditorMode;
  tab: FleetGraphEditorTab;
  saving: boolean;
  error: string | null;
  configSnapshot: Record<string, unknown> | null;
  agentDraft: FleetGraphAgentDraft;
  configDraft: FleetGraphConfigDraft;
  behaviorDraft: FleetGraphBehaviorDraft;
  bindings: FleetGraphBindingDraft[];
  agentIds: string[];
  skillsReport: SkillStatusReport | null;
  effectiveModelLabel: string;
  canDelete: boolean;
  onClose: () => void;
  onTabChange: (tab: FleetGraphEditorTab) => void;
  onAgentDraftChange: (patch: Partial<FleetGraphAgentDraft>) => void;
  onConfigDraftChange: (patch: Partial<FleetGraphConfigDraft>) => void;
  onBehaviorDraftChange: (patch: Partial<FleetGraphBehaviorDraft>) => void;
  onToggleAllowAgent: (agentId: string) => void;
  onToggleSkill: (skillName: string) => void;
  onSkillsAllowAll: (allowAll: boolean) => void;
  onBindingChange: (index: number, patch: Partial<FleetGraphBindingDraft>) => void;
  onAddBinding: (agentId?: string) => void;
  onRemoveBinding: (index: number) => void;
  onMention?: (agentId: string) => void;
  onDelete?: () => void;
  onSaveBehavior?: () => void;
  onSubmit: () => void;
};

function titleForMode(mode: FleetGraphEditorMode): string {
  if (mode === "create") {
    return "Add agent";
  }
  if (mode === "inbound") {
    return "Inbound routes";
  }
  return "Edit agent";
}

function renderTopologyTab(props: FleetGraphEditorModalProps) {
  const draft = props.agentDraft;
  const peers = props.agentIds.filter((id) => id !== draft.id.trim());
  const idLocked = props.mode === "agent";

  return html`
    <div class="agents-editor-form">
      <label class="field">
        <span>Agent id</span>
        <input
          class="mono"
          .value=${draft.id}
          ?disabled=${idLocked || props.saving}
          placeholder="e.g. research"
          @input=${(e: Event) =>
            props.onAgentDraftChange({ id: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="field">
        <span>Display name</span>
        <input
          .value=${draft.name}
          ?disabled=${props.saving}
          @input=${(e: Event) =>
            props.onAgentDraftChange({ name: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="field">
        <span>Workspace path</span>
        <input
          class="mono"
          .value=${draft.workspace}
          ?disabled=${props.saving}
          placeholder="e.g. ~/agents/research"
          @input=${(e: Event) =>
            props.onAgentDraftChange({ workspace: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="field agents-editor-checkbox">
        <input
          type="checkbox"
          .checked=${draft.setDefault}
          ?disabled=${props.saving}
          @change=${(e: Event) =>
            props.onAgentDraftChange({ setDefault: (e.target as HTMLInputElement).checked })}
        />
        <span>Set as instance default</span>
      </label>
      <div class="fleet-graph-editor-section">
        <h3 class="fleet-graph-editor-section-title">Delegate to (spawn arrows)</h3>
        <label class="field agents-editor-checkbox">
          <input
            type="checkbox"
            .checked=${draft.allowAll}
            ?disabled=${props.saving}
            @change=${(e: Event) =>
              props.onAgentDraftChange({ allowAll: (e.target as HTMLInputElement).checked })}
          />
          <span>All agents (*)</span>
        </label>
        ${
          draft.allowAll
            ? nothing
            : html`
                <div class="fleet-graph-editor-checklist">
                  ${peers.map(
                    (peerId) => html`
                      <label class="fleet-graph-editor-check">
                        <input
                          type="checkbox"
                          .checked=${draft.allowAgents.includes(peerId)}
                          ?disabled=${props.saving}
                          @change=${() => props.onToggleAllowAgent(peerId)}
                        />
                        <span class="mono">${peerId}</span>
                      </label>
                    `,
                  )}
                </div>
              `
        }
      </div>
    </div>
  `;
}

function renderConfigTab(props: FleetGraphEditorModalProps) {
  const draft = props.configDraft;
  const models = resolveConfiguredModels(props.configSnapshot);
  const skills = props.skillsReport?.skills ?? [];
  const allowAllSkills = draft.skillsAllowlist === null;
  const selectedSkills = draft.skillsAllowlist ?? [];

  return html`
    <div class="agents-editor-form">
      <p class="muted" style="font-size: 13px; margin: 0 0 12px;">
        Effective model when unset:
        <span class="mono">${props.effectiveModelLabel}</span>
      </p>
      <label class="field">
        <span>Primary model override</span>
        <select
          .value=${draft.modelPrimary ?? ""}
          ?disabled=${props.saving}
          @change=${(e: Event) =>
            props.onConfigDraftChange({
              modelPrimary: (e.target as HTMLSelectElement).value || null,
            })}
        >
          <option value="">Inherit default</option>
          ${models.map((m) => html`<option value=${m.value}>${m.label}</option>`)}
        </select>
      </label>
      <div class="fleet-graph-editor-section">
        <h3 class="fleet-graph-editor-section-title">Tool profile</h3>
        <div class="agent-tools-buttons">
          ${AGENT_TOOL_PROFILE_OPTIONS.map(
            (opt) => html`
              <button
                type="button"
                class="btn btn--sm ${draft.toolsProfile === opt.id ? "active" : ""}"
                ?disabled=${props.saving}
                @click=${() => props.onConfigDraftChange({ toolsProfile: opt.id })}
              >
                ${opt.label}
              </button>
            `,
          )}
          <button
            type="button"
            class="btn btn--sm"
            ?disabled=${props.saving}
            @click=${() => props.onConfigDraftChange({ toolsProfile: null })}
          >
            Inherit
          </button>
        </div>
      </div>
      <div class="fleet-graph-editor-section">
        <h3 class="fleet-graph-editor-section-title">Skills allowlist</h3>
        <label class="field agents-editor-checkbox">
          <input
            type="checkbox"
            .checked=${allowAllSkills}
            ?disabled=${props.saving}
            @change=${(e: Event) => props.onSkillsAllowAll((e.target as HTMLInputElement).checked)}
          />
          <span>All skills</span>
        </label>
        ${
          allowAllSkills
            ? nothing
            : html`
                <div class="fleet-graph-editor-checklist">
                  ${
                    skills.length === 0
                      ? html`
                          <p class="muted">No skills reported — check gateway.</p>
                        `
                      : skills.map(
                          (skill) => html`
                          <label class="fleet-graph-editor-check">
                            <input
                              type="checkbox"
                              .checked=${selectedSkills.includes(skill.name)}
                              ?disabled=${props.saving}
                              @change=${() => props.onToggleSkill(skill.name)}
                            />
                            <span>${skill.name}</span>
                          </label>
                        `,
                        )
                  }
                </div>
              `
        }
      </div>
      <div class="agents-editor-fields">
        <label class="field">
          <span>Identity name</span>
          <input
            .value=${draft.identityName}
            ?disabled=${props.saving}
            @input=${(e: Event) =>
              props.onConfigDraftChange({ identityName: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label class="field">
          <span>Emoji</span>
          <input
            .value=${draft.identityEmoji}
            ?disabled=${props.saving}
            @input=${(e: Event) =>
              props.onConfigDraftChange({ identityEmoji: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label class="field">
          <span>Theme</span>
          <input
            .value=${draft.identityTheme}
            ?disabled=${props.saving}
            @input=${(e: Event) =>
              props.onConfigDraftChange({ identityTheme: (e.target as HTMLInputElement).value })}
          />
        </label>
      </div>
    </div>
  `;
}

function renderBehaviorTab(props: FleetGraphEditorModalProps) {
  return html`
    <div class="agents-editor-form">
      <p class="muted" style="font-size: 13px;">
        SOUL.md sets persona; AGENTS.md sets operating rules. Injected each session from this agent's
        workspace.
      </p>
      <label class="field">
        <span>SOUL.md</span>
        <textarea
          class="fleet-graph-editor-textarea mono"
          rows="10"
          .value=${props.behaviorDraft.soul}
          ?disabled=${props.saving}
          @input=${(e: Event) =>
            props.onBehaviorDraftChange({ soul: (e.target as HTMLTextAreaElement).value })}
        ></textarea>
      </label>
      <label class="field">
        <span>AGENTS.md</span>
        <textarea
          class="fleet-graph-editor-textarea mono"
          rows="10"
          .value=${props.behaviorDraft.agents}
          ?disabled=${props.saving}
          @input=${(e: Event) =>
            props.onBehaviorDraftChange({ agents: (e.target as HTMLTextAreaElement).value })}
        ></textarea>
      </label>
      ${
        props.onSaveBehavior
          ? html`
              <button
                type="button"
                class="btn"
                ?disabled=${props.saving}
                @click=${() => props.onSaveBehavior?.()}
              >
                Save behavior files now
              </button>
            `
          : nothing
      }
    </div>
  `;
}

function renderBindingsForm(props: FleetGraphEditorModalProps) {
  return html`
    <div class="fleet-graph-editor-bindings">
      ${
        props.bindings.length === 0
          ? html`
              <p class="muted">No inbound routes yet.</p>
            `
          : props.bindings.map(
              (row, index) => html`
                <div class="fleet-graph-editor-binding-card">
                  <div class="fleet-graph-editor-binding-header">
                    <span class="mono">${row.bindingId || "new-route"}</span>
                    <button
                      type="button"
                      class="btn danger subtle"
                      ?disabled=${props.saving}
                      @click=${() => props.onRemoveBinding(index)}
                    >
                      Remove
                    </button>
                  </div>
                  <label class="field">
                    <span>Binding id</span>
                    <input
                      class="mono"
                      .value=${row.bindingId}
                      @input=${(e: Event) =>
                        props.onBindingChange(index, {
                          bindingId: (e.target as HTMLInputElement).value,
                        })}
                    />
                  </label>
                  <label class="field">
                    <span>Target agent</span>
                    <select
                      .value=${row.agentId}
                      @change=${(e: Event) =>
                        props.onBindingChange(index, {
                          agentId: (e.target as HTMLSelectElement).value,
                        })}
                    >
                      <option value="">Select…</option>
                      ${props.agentIds.map((id) => html`<option value=${id}>${id}</option>`)}
                    </select>
                  </label>
                  <label class="field">
                    <span>Keywords (comma-separated)</span>
                    <input
                      .value=${row.keywords}
                      @input=${(e: Event) =>
                        props.onBindingChange(index, {
                          keywords: (e.target as HTMLInputElement).value,
                        })}
                    />
                  </label>
                </div>
              `,
            )
      }
      <button type="button" class="btn" @click=${() => props.onAddBinding()}>Add route</button>
    </div>
  `;
}

export function renderFleetGraphEditorModal(props: FleetGraphEditorModalProps) {
  if (!props.open) {
    return nothing;
  }

  const isInbound = props.mode === "inbound";
  const showTabs = !isInbound;
  const canMention = props.mode === "agent" && Boolean(props.onMention && props.agentDraft.id);

  return html`
    <div
      class="agents-editor-overlay fleet-graph-editor-overlay"
      role="dialog"
      aria-modal="true"
      @click=${(e: Event) => {
        if (e.target === e.currentTarget) {
          props.onClose();
        }
      }}
    >
      <div class="agents-editor-card fleet-graph-editor-card" @click=${(e: Event) => e.stopPropagation()}>
        <header class="agents-editor-header">
          <div>
            <h2 class="agents-editor-title">${titleForMode(props.mode)}</h2>
            <p class="agents-editor-sub">
              ${
                isInbound
                  ? "Dashed arrows on the map (workforce.bindings)."
                  : "Topology, config, and behavior — saved to gateway."
              }
            </p>
          </div>
          <button type="button" class="btn icon" aria-label="Close" @click=${props.onClose}>
            ${icons.x}
          </button>
        </header>
        ${
          props.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
            : nothing
        }
        ${
          showTabs
            ? html`
                <nav class="fleet-graph-editor-tabs" aria-label="Agent editor sections">
                  ${(["topology", "config", "behavior"] as const).map(
                    (t) => html`
                      <button
                        type="button"
                        class="fleet-graph-editor-tabs__btn ${props.tab === t ? "fleet-graph-editor-tabs__btn--active" : ""}"
                        @click=${() => props.onTabChange(t)}
                      >
                        ${t === "topology" ? "Topology" : t === "config" ? "Config" : "Behavior"}
                      </button>
                    `,
                  )}
                </nav>
              `
            : nothing
        }
        ${
          isInbound
            ? renderBindingsForm(props)
            : props.tab === "topology"
              ? renderTopologyTab(props)
              : props.tab === "config"
                ? renderConfigTab(props)
                : renderBehaviorTab(props)
        }
        <p class="muted" style="font-size: 12px; margin-top: 12px;">
          ${
            props.tab === "behavior"
              ? "Behavior files can be saved immediately or with Save below."
              : "Config and topology save together on Save."
          }
        </p>
        <div class="agents-editor-actions fleet-graph-editor-actions">
          ${
            props.canDelete && props.onDelete
              ? html`
                  <button type="button" class="btn danger" ?disabled=${props.saving} @click=${props.onDelete}>
                    Delete agent
                  </button>
                `
              : nothing
          }
          <span class="fleet-graph-editor-actions-spacer"></span>
          ${
            canMention
              ? html`
                  <button
                    type="button"
                    class="btn"
                    ?disabled=${props.saving}
                    @click=${() => props.onMention?.(props.agentDraft.id)}
                  >
                    @mention
                  </button>
                `
              : nothing
          }
          <button type="button" class="btn" ?disabled=${props.saving} @click=${props.onClose}>
            Cancel
          </button>
          <button
            type="button"
            class="btn primary"
            ?disabled=${props.saving || (!isInbound && !props.agentDraft.id.trim())}
            @click=${props.onSubmit}
          >
            ${props.saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  `;
}
